import { useState, useEffect } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Plug,
  RefreshCw,
  Check,
  X,
  ExternalLink,
  Settings,
  Link2,
  Unlink,
  TestTube,
  History,
  Globe,
  Shield,
  Zap,
  Star,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Clock,
  Database,
  Webhook
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Platform logos (fallback to initials)
const getPlatformLogo = (platform, logoUrl) => {
  if (logoUrl && !logoUrl.startsWith('/')) {
    return logoUrl;
  }
  return null;
};

const getPlatformInitials = (name) => {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const platformColors = {
  opentable: 'from-red-500 to-red-600',
  resy: 'from-blue-500 to-blue-600',
  reservandonos: 'from-green-500 to-green-600',
  thefork: 'from-teal-500 to-teal-600',
  yelp: 'from-red-600 to-red-700',
  google: 'from-blue-400 to-green-500',
  tripadvisor: 'from-green-600 to-green-700',
  covermanager: 'from-purple-500 to-purple-600',
  quandoo: 'from-orange-500 to-orange-600',
  bookatable: 'from-indigo-500 to-indigo-600',
  mesa247: 'from-yellow-500 to-yellow-600'
};

export default function Integrations() {
  const [platforms, setPlatforms] = useState({});
  const [integrations, setIntegrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Config form
  const [configForm, setConfigForm] = useState({
    api_key: '',
    api_secret: '',
    restaurant_id_external: '',
    sync_enabled: false,
    sync_reservations: true,
    sync_availability: true,
    sync_reviews: false
  });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch platforms
      const platformsRes = await fetch(`${API_URL}/api/integrations/platforms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (platformsRes.ok) {
        setPlatforms(await platformsRes.json());
      }
      
      // Fetch integrations
      const integrationsRes = await fetch(`${API_URL}/api/integrations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (integrationsRes.ok) {
        setIntegrations(await integrationsRes.json());
      }
      
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/integrations/stats/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (err) {
      console.error('Error fetching integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getIntegrationForPlatform = (platformKey) => {
    return integrations.find(i => i.platform === platformKey);
  };

  const openConfigDialog = (platformKey) => {
    const existing = getIntegrationForPlatform(platformKey);
    setSelectedPlatform(platformKey);
    setConfigForm({
      api_key: existing?.api_key || '',
      api_secret: '',  // Never show existing secret
      restaurant_id_external: existing?.restaurant_id_external || '',
      sync_enabled: existing?.sync_enabled || false,
      sync_reservations: existing?.sync_reservations ?? true,
      sync_availability: existing?.sync_availability ?? true,
      sync_reviews: existing?.sync_reviews || false
    });
    setShowConfigDialog(true);
  };

  const saveConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/integrations/${selectedPlatform}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          platform: selectedPlatform,
          ...configForm
        })
      });
      
      if (res.ok) {
        toast.success('Configuración guardada');
        setShowConfigDialog(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al guardar');
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
  };

  const testConnection = async (platformKey) => {
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/integrations/${platformKey}/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error en la prueba');
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setTesting(false);
    }
  };

  const syncPlatform = async (platformKey) => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/integrations/${platformKey}/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al sincronizar');
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setSyncing(false);
    }
  };

  const disconnectPlatform = async (platformKey) => {
    if (!confirm('¿Desconectar esta integración?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/integrations/${platformKey}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Integración desconectada');
        fetchData();
      }
    } catch (err) {
      toast.error('Error al desconectar');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="integrations-page">
      <TopBar
        title="Integraciones"
        subtitle="Conecta con plataformas externas de reservas"
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={fetchData}
            className="rounded-xl"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        }
      />

      <div className="p-6">
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Plug className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_integrations}</p>
                    <p className="text-sm text-muted-foreground">Configuradas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.active_integrations}</p>
                    <p className="text-sm text-muted-foreground">Activas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Database className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_syncs}</p>
                    <p className="text-sm text-muted-foreground">Sincronizaciones</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Globe className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.available_platforms?.length || 0}</p>
                    <p className="text-sm text-muted-foreground">Plataformas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Platforms Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Object.entries(platforms).map(([key, platform]) => {
            const integration = getIntegrationForPlatform(key);
            const isConnected = integration?.api_key;
            const isActive = integration?.sync_enabled;
            
            return (
              <Card 
                key={key} 
                className={cn(
                  "border-0 shadow-sm transition-all hover:shadow-md",
                  isConnected && "ring-2 ring-green-200"
                )}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Platform Logo/Icon */}
                    <div className={cn(
                      "w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-lg bg-gradient-to-br",
                      platformColors[key] || 'from-gray-500 to-gray-600'
                    )}>
                      {getPlatformInitials(platform.name)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{platform.name}</h3>
                        {isConnected && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <Check className="h-3 w-3 mr-1" /> Conectado
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {platform.description}
                      </p>
                      
                      {/* Features */}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {platform.features?.map((feature) => (
                          <Badge key={feature} variant="secondary" className="text-xs">
                            {feature}
                          </Badge>
                        ))}
                      </div>
                      
                      {/* Regions */}
                      <p className="text-xs text-muted-foreground mt-2">
                        Regiones: {platform.regions?.join(', ')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                    {isConnected ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => testConnection(key)}
                          disabled={testing}
                          className="flex-1"
                        >
                          <TestTube className="h-4 w-4 mr-1" />
                          Probar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => syncPlatform(key)}
                          disabled={syncing || !isActive}
                          className="flex-1"
                        >
                          <RefreshCw className={cn("h-4 w-4 mr-1", syncing && "animate-spin")} />
                          Sync
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openConfigDialog(key)}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => disconnectPlatform(key)}
                          className="text-destructive"
                        >
                          <Unlink className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          onClick={() => openConfigDialog(key)}
                          className="flex-1"
                        >
                          <Link2 className="h-4 w-4 mr-1" />
                          Conectar
                        </Button>
                        {platform.website && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(platform.website, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Sync Status */}
                  {isConnected && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Sincronización</span>
                        <Badge variant={isActive ? 'default' : 'secondary'}>
                          {isActive ? 'Activa' : 'Pausada'}
                        </Badge>
                      </div>
                      {integration?.last_sync && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Última sync: {new Date(integration.last_sync).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configurar {platforms[selectedPlatform]?.name}
            </DialogTitle>
            <DialogDescription>
              Ingresa tus credenciales de API para conectar con {platforms[selectedPlatform]?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>API Key *</Label>
              <Input
                type="password"
                value={configForm.api_key}
                onChange={(e) => setConfigForm(prev => ({ ...prev, api_key: e.target.value }))}
                placeholder="Tu API Key"
              />
            </div>
            
            <div>
              <Label>API Secret</Label>
              <Input
                type="password"
                value={configForm.api_secret}
                onChange={(e) => setConfigForm(prev => ({ ...prev, api_secret: e.target.value }))}
                placeholder="Tu API Secret (si aplica)"
              />
            </div>
            
            <div>
              <Label>ID de Restaurante Externo</Label>
              <Input
                value={configForm.restaurant_id_external}
                onChange={(e) => setConfigForm(prev => ({ ...prev, restaurant_id_external: e.target.value }))}
                placeholder="ID en la plataforma externa"
              />
              <p className="text-xs text-muted-foreground mt-1">
                El ID de tu restaurante en {platforms[selectedPlatform]?.name}
              </p>
            </div>
            
            <div className="space-y-3 pt-4 border-t">
              <h4 className="font-medium">Opciones de Sincronización</h4>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Habilitar sincronización</Label>
                  <p className="text-xs text-muted-foreground">Sincronizar datos automáticamente</p>
                </div>
                <Switch
                  checked={configForm.sync_enabled}
                  onCheckedChange={(checked) => setConfigForm(prev => ({ ...prev, sync_enabled: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Sincronizar reservaciones</Label>
                </div>
                <Switch
                  checked={configForm.sync_reservations}
                  onCheckedChange={(checked) => setConfigForm(prev => ({ ...prev, sync_reservations: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Sincronizar disponibilidad</Label>
                </div>
                <Switch
                  checked={configForm.sync_availability}
                  onCheckedChange={(checked) => setConfigForm(prev => ({ ...prev, sync_availability: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Sincronizar reseñas</Label>
                </div>
                <Switch
                  checked={configForm.sync_reviews}
                  onCheckedChange={(checked) => setConfigForm(prev => ({ ...prev, sync_reviews: checked }))}
                />
              </div>
            </div>
            
            {platforms[selectedPlatform]?.requires_approval && (
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Requiere aprobación</p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Esta plataforma requiere que solicites acceso a su API. Visita su portal de partners.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={saveConfig} disabled={!configForm.api_key}>
              Guardar Configuración
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
